// src/icon-system/runtime/motorYukleyici.ts — MOTOR KOLUNUN TEK TEMBEL KAPISI (Faz C / tembel köprü)
//
// ── NEDEN VAR (ölçüldü, 2026-09-07) ──────────────────────────────────────
// Dalga 1 (+2 631 B gzip) ve dalga 2 (+2 603 B gzip) motor aile bileşenlerini
// `import { NavigationHomeIkon } from "…/generated/NavigationHome"` ile EAGER ilk pakete
// soktu: 390 910 B gzip / tavan 393 390 → kalan 2 480 B. Bayrak (`mylasa.ff.ikon`)
// VARSAYILAN KAPALI, yani kullanıcı motoru hiç görmeden baytını ödüyordu; dalga 3
// (profil, 83 aday) bu yapıyla tavanı aşardı. Çare: motor kolu TEK dinamik import'un
// arkasında. Bayrak kapalıyken bu modülün yükleme fonksiyonu HİÇ çağrılmaz →
// `generated/*` hiçbir eager modülden erişilemez (paket sözleşmesi bunu AST + çağrı
// sayacıyla ölçer: `src/__tests__/ikonKopruPaketSozlesmesi.test.ts`).
//
// ── TEK CHUNK mu, DALGA BAŞINA CHUNK mu — KARAR: TEK CHUNK (`ikon-motor`) ──
// · 78 ailenin tamamı ≈ 18,7 kB gzip kaynak; bayrak AÇIKKEN bile ilk paket DEĞİL,
//   ayrı ve önbelleklenebilir tek istek (hash'li ad → ikinci ekranda 0 istek).
// · Dalga başına chunk, ekran başına 1-3 ek istek demek olurdu VE aileler dalgalar
//   arasında ORTAK (navigation.clips dalga 1+2; content.save dalga 2+3…) → webpack ya
//   kopyalar ya öngörülemez adlı ortak-chunk üretir; ikincisinde `ilk-paket-kapisi.js`
//   adlı-chunk ölçümü kör kalırdı (kanon: ölçülemeyen bölme, ölçülmeyen bölmedir).
// · Bölmenin gerçek bedeli ölçülür ve raporlanır (`ikon-motor` chunk'ı + ilk paket ∆).
//
// ── NİYE `React.lazy` + `Suspense` DEĞİL ─────────────────────────────────
// `React.lazy` chunk önbellekteyken bile ilk render'da askıya alır (fallback bir tick
// görünür) ve `renderToStaticMarkup`/izole test kayıtlarında fallback'e düşer. Burada
// modül bir kez indikten sonra `motorModulu()` SENKRON döner: köprü, chunk hazırsa ilk
// render'da doğrudan motoru çizer. Hazır değilse ESKİ ikonu çizer (aynı kutu) ve chunk
// gelince BİR kez yeniden çizer — kabul edilen tek geçiş; boş/atlayan kare yok, CLS 0
// (GÖZ perf-metrics ile ölçülür).
//
// Yükleme hatası (chunk 404 / ağ) → `eski`de KALINIR (güvenli yön), bir kez `warn`,
// sonraki mount yeniden dener. Sessiz boşluk YOK.
//
// ── ÖNBELLEK NİYE `globalThis`TE ─────────────────────────────────────────
// `jest.isolateModules` her kolda YENİ bir modül kaydı kurar; önbellek modül-yerel
// olsaydı her izole mutasyon kolu chunk'ı yeniden yükleyip ilk render'da `eski` çizerdi
// ve kolların hükmü "tembellik" değil "izolasyon" ölçerdi. Üründe tek kayıt vardır,
// yani bu seçim davranışı değiştirmez; ölçümü kararlı kılar.
import { useEffect, useReducer } from "react";
import { motorYayinaUygun } from "./yayinKapisi";

/** Runtime contract includes explicitly registered, language-specific families. */
import type { MotorModulu } from "./motorSozlesmesi";
export type { MotorModulu } from "./motorSozlesmesi";

/** Üretilmiş aile bileşeninin genel şekli (aileye özel literal tipler köprünün prop'unda korunur). */
export type MotorBileseni = React.ComponentType<Record<string, unknown>>;

type Onbellek = { modul: MotorModulu | null; yukleme: Promise<MotorModulu> | null; uyarildi: boolean };
const KUTU = globalThis as unknown as { __mylasaIkonMotor?: Onbellek };
const onbellek: Onbellek = (KUTU.__mylasaIkonMotor ||= { modul: null, yukleme: null, uyarildi: false });

/** Yüklenmiş motor modülü — SENKRON; henüz inmediyse `null` (köprü `eski`yi çizer). */
export function motorModulu(): MotorModulu | null {
  return onbellek.modul;
}

/**
 * Motor chunk'ını (bir kez) indirir. Bayrak KAPALIYKEN ÇAĞRILMAZ — köprü ve adaptörler
 * yalnız `ikonMotoruAcikMi()` doğruyken çağırır.
 */
export function motorModulunuYukle(): Promise<MotorModulu> {
  if (onbellek.modul) return Promise.resolve(onbellek.modul);
  if (!onbellek.yukleme) {
    const yukleme = process.env.REACT_APP_FF_IKON_DIL === "outline-v1"
      ? import(/* webpackChunkName: "ikon-outline-v1" */ "../outline-v1").then((m) => m.motorOutline)
      : process.env.REACT_APP_FF_IKON_DIL === "fold-v2"
      ? import(/* webpackChunkName: "ikon-fold-v2" */ "../fold-v2").then((m) => m.motorV2)
      : import(/* webpackChunkName: "ikon-motor" */ "../generated");
    onbellek.yukleme = yukleme
      .then((m) => {
        onbellek.modul = m;
        return m;
      })
      .catch((e: unknown) => {
        onbellek.yukleme = null; // sonraki mount yeniden dener
        if (!onbellek.uyarildi) {
          onbellek.uyarildi = true;
          // eslint-disable-next-line no-console
          console.warn(`[ikon] motor chunk'ı yüklenemedi → eski ikonlar çizilmeye devam ediyor: ${e instanceof Error ? e.message : String(e)}`);
        }
        throw e;
      });
  }
  return onbellek.yukleme;
}

/**
 * Köprü/adaptör kancası. `gerekli` (bayrak açık) ise chunk'ı ister ve geldiğinde bileşeni
 * BİR kez yeniden çizer; modül zaten önbellekteyse hiç yüklemez (render senkron motoru
 * çizmiştir). `gerekli=false` (bayrak kapalı) iken dinamik import HİÇ tetiklenmez ve
 * dönüş DAİMA `null`dur — önbellekte modül olsa bile (kapalı bayrak = eski ikon).
 */
export function useMotorModulu(gerekli: boolean): MotorModulu | null {
  const [, yenile] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!gerekli || onbellek.modul) return undefined;
    let canli = true;
    motorModulunuYukle().then(
      () => { if (canli) yenile(); },
      () => { /* eski'de kal (warn yukarıda) */ },
    );
    return () => { canli = false; };
  }, [gerekli]);
  return gerekli ? onbellek.modul : null;
}

/**
 * Aile adı (`navigation.home`; sözlük adı `action.create` dâhil) → yüklü modüldeki bileşen;
 * yoksa null. YAYIN KAPISI burada da sorulur (`yayinKapisi.motorYayinaUygun`): köprü ve
 * adaptörler kapıyı kendi katmanında sorsa da, bileşene ulaşan TEK fonksiyon budur —
 * kapıyı aşan bir çağrı yolu (yeni adaptör, laboratuvar) buradan geçemez. İşaretler
 * chunk'ın İÇİNDEDİR (`IKON_BILGISI`), eager pakete ad listesi kopyalanmaz.
 */
export function motorBileseni(m: MotorModulu, aile: string, varyant = "outline"): MotorBileseni | null {
  if (!motorYayinaUygun(m, aile, varyant)) return null;
  const aileId = m.aileAdiCoz(aile);
  const bilgi = aileId ? m.IKON_BILGISI[aileId] : undefined;
  if (!bilgi) return null;
  const c = (m as unknown as Record<string, unknown>)[bilgi.bilesen];
  return typeof c === "function" ? (c as MotorBileseni) : null;
}

/**
 * Üretilmiş bileşene geçilecek `kontur` prop'u. Seçim VARKEN boş (bileşen prop'u
 * düşürdü; geçilirse `<svg kontur>` DOM özniteliğine sızar — ölçüldü, Faz B), seçim
 * yokken adayların ilki. Adaptörlerin `KONTUR_PROPU` sabitiyle aynı kural; oradaki
 * `uyumluluk.json` varsayılanı yerine modülün kendi aday listesi okunur ki bu EAGER
 * modül 9 kB'lık sözleşme JSON'unu ilk pakete çekmesin.
 */
export function konturPropu(m: MotorModulu): { kontur?: string } {
  if (m.SECILEN_KONTUR) return {};
  const ilk = Object.keys(m.KONTUR_ADAYLARI)[0];
  return ilk ? { kontur: ilk } : {};
}
